import {
    getSelectedTableColumns,
    getSystemColumnItems,
    isTableVisibleCatalogItem,
} from '../ScenarioLensSection/scenarioLensCatalog.helpers';
import type { LensCatalogData, LensCatalogItem } from '../ScenarioLensSection/scenarioLensCatalog.types';
import type { LensTableRow } from './buildLensTableColumns';

export const DEFAULT_LENS_PLACEHOLDER_ROW_COUNT = 5;

const KPI_SUB_COLUMNS = ['current', 'chg', 'new'] as const;

export const SYSTEM_COLUMN_TILE_ROTATION = ['Price', 'Pack', 'Boost', '', ''] as const;

const normalizePlaceholderPrefix = (value: string): string => {
    const normalized = value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '');

    return normalized || 'dim';
};

/** e.g. brand -> Brand1, channel -> Channel2 */
export const buildDimensionPlaceholderValue = (dimension: LensCatalogItem, rowIndex: number): string => {
    const prefix = normalizePlaceholderPrefix(dimension.attribute_key || dimension.display_name);
    const capitalizedPrefix = prefix.charAt(0).toUpperCase() + prefix.slice(1);
    return `${capitalizedPrefix}${rowIndex + 1}`;
};

export const buildSystemColumnPlaceholderValue = (rowIndex: number): string =>
    SYSTEM_COLUMN_TILE_ROTATION[rowIndex % SYSTEM_COLUMN_TILE_ROTATION.length];

/** Stable pseudo-random integer in [1, 10] for preview cells. */
export const buildKpiPlaceholderValue = (seed: string): number => {
    let hash = 0;

    for (let index = 0; index < seed.length; index += 1) {
        hash = (hash << 5) - hash + seed.charCodeAt(index);
        hash |= 0;
    }

    return (Math.abs(hash) % 10) + 1;
};

export type BuildLensPlaceholderRowsOptions = {
    rowCount?: number;
};

export const buildLensPlaceholderRows = (
    catalog: LensCatalogData,
    { rowCount = DEFAULT_LENS_PLACEHOLDER_ROW_COUNT }: BuildLensPlaceholderRowsOptions = {},
): LensTableRow[] => {
    const { dimensions, kpis } = getSelectedTableColumns(catalog);
    const systemColumns = getSystemColumnItems(catalog).filter(isTableVisibleCatalogItem);

    return Array.from({ length: rowCount }, (_, rowIndex) => {
        const row: LensTableRow = {
            key: `placeholder-row-${rowIndex}`,
        };

        dimensions.forEach((dimension) => {
            row[dimension.attribute_key] = buildDimensionPlaceholderValue(dimension, rowIndex);
        });

        kpis.forEach((kpi) => {
            KPI_SUB_COLUMNS.forEach((subColumn) => {
                const fieldKey = `${kpi.attribute_key}_${subColumn}`;
                row[fieldKey] = buildKpiPlaceholderValue(`${fieldKey}-${rowIndex}`);
            });
        });

        systemColumns.forEach((systemColumn) => {
            row[systemColumn.attribute_key] = buildSystemColumnPlaceholderValue(rowIndex);
        });

        return row;
    });
};
